import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import TLogo from '../../assets/appLogoFinalDark.png';

// Heroicons imports
import {
  CheckCircleIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
  MapIcon,
  ChartBarIcon,
  SparklesIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

import {
  CheckIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/solid';

const OnboardingWizard = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [emailError, setEmailError] = useState('');
  
  // Email confirmation state
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loadingAction, setLoadingAction] = useState('');
  const otpInputRef = useRef('');
  const [emailSendAttempted, setEmailSendAttempted] = useState(false);
  const [emailResent, setEmailResent] = useState(false);


  
  // Main form data - only updated onBlur
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyOption: 'create',
    companyName: '',
    companyKey: '',
    acceptTerms: false,
    acceptedCompanyName: ''
  });

  // Refs for input values - no re-renders
  const inputRefs = useRef({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyKey: ''
  });

  const steps = [
    { id: 1, name: 'Account', description: 'Your information' },
    { id: 2, name: 'Verify', description: 'Confirm email' },
    { id: 3, name: 'Company', description: 'Workspace setup' },
    { id: 4, name: 'Welcome', description: 'You\'re all set!' }
  ];

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password);
  };

  // Handle blur events (actual state updates and validation)
  const handleInputBlur = async (field, value) => {
    // Update main form data
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear previous validation errors for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Special handling for email field
    if (field === 'email') {
      setEmailError('');
      
      if (value && validateEmail(value)) {
        // Check if email already exists
        try {
          const { data: existingUser, error: emailCheckError } = await supabase
            .from('profile')
            .select('email_address')
            .eq('email_address', value)
            .single();

          if (existingUser) {
            setEmailError('Email already exists.');
          } else if (emailCheckError && emailCheckError.code !== 'PGRST116') {
            throw emailCheckError;
          }
        } catch (err) {
          setEmailError('An unexpected error occurred while checking the email.');
        }
      }
    }
  };

  // Handle non-text input changes (checkboxes, radio buttons)
  const handleNonTextInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation errors when user makes changes
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Check for pending invites when reaching step 3 and email is valid
  useEffect(() => {
    const checkInvites = async () => {
      if (currentStep === 3 && formData.email && validateEmail(formData.email)) {
        await checkPendingInvites(formData.email);
      }
    };

    checkInvites();
  }, [currentStep, formData.email]);

  const checkPendingInvites = async (email) => {
    setInvitesLoading(true);
    setInviteError('');
    
    try {
      const { data, error } = await supabase
        .from('organization_invites')
        .select('id, organization_id, organizations(name), role')
        .eq('email', email)
        .eq('status', 'pending')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const invite = data ? {
        id: data.id,
        organization_id: data.organization_id,
        organizations: { name: data.organizations.name },
        inviter_email: 'team@company.com',
        role: data.role
      } : null;

      setPendingInvites(invite ? [invite] : []);
    } catch (err) {
      console.error('Error checking for invites:', err);
      setPendingInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  };

  // Email confirmation functions
  const handleSendConfirmationEmail = async () => {
    try {
      setOtpError('');
      setOtpMessage('');
      setLoadingAction('send');
      setEmailSendAttempted(true); // Track that we've attempted to send
      
      const { error } = await supabase.auth.signInWithOtp({
        email: formData.email,
        options: { emailRedirectTo: window.location.origin },
      });
      
      if (error) {
        console.error('Error sending OTP:', error.message);
        setOtpError(`Failed to send code: ${error.message}`);
      } else {
        setOtpMessage('We just sent a code to your email. Enter it below to confirm.');
        setOtpStep(true);
        setEmailResent(true); // Set resent state
      }
    } catch (err) {
      console.error('Unexpected error sending OTP:', err.message);
      setOtpError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoadingAction('');
    }
  };

  useEffect(() => {
    if (emailResent) {
      const timer = setTimeout(() => {
        setEmailResent(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [emailResent]);

  const handleSkipEmailConfirmation = () => {
    setCurrentStep(3); // Go to company step without verification
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpError('');
      setOtpMessage('');
      setLoadingAction('verify');
      
      const { error } = await supabase.auth.verifyOtp({
        email: formData.email,
        token: otp.trim(),
        type: 'email',
      });
      
      if (error) {
        console.error('OTP verification failed:', error.message);
        setOtpError(`Code verification failed: ${error.message}`);
      } else {
        setOtpMessage('Your email has been confirmed!');
        
        // Update profile to mark email as confirmed
        const { error: updateError } = await supabase
          .from('profile')
          .update({ is_email_confirmed: true })
          .eq('id', userId);
          
        if (updateError) {
          console.error('Error updating email confirmation in profile:', updateError.message);
        }
        
        // Clear OTP state and proceed to next step
        setOtp('');
        setOtpStep(false);
        setCurrentStep(3);
      }
    } catch (err) {
      console.error('Unexpected error verifying OTP:', err.message);
      setOtpError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction('');
    }
  };

  const handleAcceptInvite = async (inviteId, organizationId, organizationName) => {
    setInvitesLoading(true);
    setInviteError('');
    setInviteSuccess('');
    
    try {
      // Add user to organization
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert([{ 
          user_id: userId, 
          organization_id: organizationId, 
          role: pendingInvites[0]?.role || 'member' 
        }]);

      if (memberError) throw memberError;

      // Update invite status
      const { error: inviteUpdateError } = await supabase
        .from('organization_invites')
        .update({ status: 'accepted' })
        .eq('id', inviteId);

      if (inviteUpdateError) throw inviteUpdateError;

      // Store the accepted company name
      setFormData(prev => ({ ...prev, acceptedCompanyName: organizationName }));
      
      // Remove invite from list
      setPendingInvites([]);
      
      // Set success message and proceed to welcome step
      setInviteSuccess(`Successfully joined ${organizationName}!`);
      setCurrentStep(4);
      
    } catch (err) {
      setInviteError('Failed to accept the invite. Please try again.');
      console.error('Error accepting invite:', err);
    } finally {
      setInvitesLoading(false);
    }
  };

  const createCompany = async (companyName) => {
    try {
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      // Call the Supabase edge function (same as legacy implementation)
      const response = await fetch('https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/create-company', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          user_id: userId,
          company_name: companyName
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to create company');
      }

      return result;
    } catch (err) {
      console.error('Error creating company:', err);
      throw err;
    }
  };

  const joinCompanyByKey = async (companyKey) => {
    try {
      // Step 1: Find organization by key
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('organization_key', companyKey)
        .single();

      if (orgError) throw new Error('Invalid company key or company not found');

      // Step 2: Check team usage (same as legacy implementation)
      const response = await fetch('https://nodejs-serverless-function-express-azure-delta.vercel.app/api/team-usage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: orgData.id }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch team usage data.');
      }

      const teamUsage = await response.json();

      // Step 3: Check if spots are available
      if (teamUsage.remainingSpots <= 0) {
        throw new Error(`No available spots in ${orgData.name}. Please contact the admin.`);
      }

      // Step 4: Add user as member of the organization
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert([{ 
          user_id: userId, 
          organization_id: orgData.id, 
          role: 'member' 
        }]);

      if (memberError) throw memberError;

      return orgData;
    } catch (err) {
      console.error('Error joining company:', err);
      throw err;
    }
  };

  const getCurrentInputValues = () => {
    return {
      firstName: inputRefs.current.firstName || formData.firstName,
      lastName: inputRefs.current.lastName || formData.lastName,
      email: inputRefs.current.email || formData.email,
      password: inputRefs.current.password || formData.password,
      confirmPassword: inputRefs.current.confirmPassword || formData.confirmPassword,
      companyName: inputRefs.current.companyName || formData.companyName,
      companyKey: inputRefs.current.companyKey || formData.companyKey,
    };
  };

  const validateStep1 = () => {
    const errors = {};
    const values = getCurrentInputValues();
    
    if (!values.firstName.trim()) errors.firstName = 'First name is required';
    if (!values.lastName.trim()) errors.lastName = 'Last name is required';
    if (!values.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(values.email)) {
      errors.email = 'Please enter a valid email address';
    } else if (emailError) {
      errors.email = emailError;
    }
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (!validatePassword(values.password)) {
      errors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.acceptTerms) {
      errors.acceptTerms = 'You must accept the terms to continue';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    const values = getCurrentInputValues();
    
    // Check if email confirmation is required for company creation
    if (formData.companyOption === 'create') {
      // Check if email is confirmed via profile or if they've verified in this session
      if (!otpMessage.includes('confirmed') && !formData.emailConfirmed) {
        errors.emailConfirmation = 'Email confirmation is required to create a company. Please verify your email first.';
      }
      if (!values.companyName.trim()) {
        errors.companyName = 'Company name is required';
      }
    }
    
    if (formData.companyOption === 'join' && !values.companyKey.trim()) {
      errors.companyKey = 'Company key is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = async () => {
    setIsSubmitting(true);
    
    try {
      if (currentStep === 1) {
        const values = getCurrentInputValues();
        
        // Update formData with current input values
        setFormData(prev => ({
          ...prev,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          confirmPassword: values.confirmPassword
        }));

        if (!validateStep1()) {
          setIsSubmitting(false);
          return;
        }

        // Create account
        const { data, error } = await supabase.auth.signUp({
          email: values.email,
          password: values.password,
          options: {
            data: { 
              first_name: values.firstName, 
              last_name: values.lastName 
            },
          },
        });

        if (error) throw error;

        const user = data.user;
        if (user) {
          setUserId(user.id);
          
          // Update profile
          const { error: profileError } = await supabase
            .from('profile')
            .update({ 
              first_name: values.firstName, 
              last_name: values.lastName 
            })
            .eq('id', user.id);

          if (profileError) console.error('Profile update error:', profileError);
        }

        setCurrentStep(2); // Go to email verification step
      } else if (currentStep === 2) {
        // Email verification step - handled by handleVerifyOtp
        // This shouldn't be called directly as verification auto-advances
        setIsSubmitting(false);
        return;
      } else if (currentStep === 3) {
        const values = getCurrentInputValues();
        
        // Update formData with current input values
        setFormData(prev => ({
          ...prev,
          companyName: values.companyName,
          companyKey: values.companyKey
        }));

        // For invite option, user clicks "Continue" which is handled by button onClick
        if (formData.companyOption === 'invite') {
          if (pendingInvites.length > 0) {
            await handleAcceptInvite(
              pendingInvites[0].id, 
              pendingInvites[0].organization_id, 
              pendingInvites[0].organizations.name
            );
          }
          return;
        }

        if (!validateStep2()) {
          setIsSubmitting(false);
          return;
        }

        if (formData.companyOption === 'create') {
          await createCompany(values.companyName);
        } else if (formData.companyOption === 'join') {
          const orgData = await joinCompanyByKey(values.companyKey);
          setFormData(prev => ({ ...prev, acceptedCompanyName: orgData.name }));
        }

        setCurrentStep(4); // Go to welcome step
      } else if (currentStep === 4) {
        // This step is now handled by the WelcomeStep button directly
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      setValidationErrors({ general: err.message || 'An unexpected error occurred.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Email Confirmation
  const EmailConfirmationStep = () => (
    <div className="max-w-md w-full bg-white rounded-xl p-8 shadow-xl border border-gray-100">
      <div className="text-center mb-8">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-4 rounded-full inline-block mb-6 shadow-lg border border-blue-500/40">
          <EnvelopeIcon className="w-10 h-10 text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
        <p className="text-gray-600 mb-4">We need to confirm your email address to secure your account</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-800">{formData.email}</p>
        </div>
      </div>

      <div className="space-y-6">
        {!otpStep ? (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Click below to send a verification code to your email address.
              </p>
            </div>
            
            <button
              onClick={handleSendConfirmationEmail}
              disabled={loadingAction === 'send'}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-blue-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loadingAction === 'send' ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sending Code...
                </>
              ) : (
                'Send Verification Code'
              )}
            </button>

            

            {otpError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">{otpError}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code we sent to your email address.
              </p>
            </div>

            <div>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg font-mono"
                placeholder="000000"
                maxLength="6"
                disabled={loadingAction === 'verify'}
              />
            </div>

            <button
              onClick={handleVerifyOtp}
              disabled={loadingAction === 'verify' || !otp.trim()}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-blue-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loadingAction === 'verify' ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?{' '}
                <button
                    onClick={handleSendConfirmationEmail}
                    className="text-blue-600 hover:text-blue-500 font-medium inline-flex items-center"
                    disabled={loadingAction === 'send' || emailResent}
                    >
                    {emailResent ? (
                        <>
                        <CheckIcon className="w-4 h-4 mr-1 text-green-600" />
                        Resent
                        </>
                    ) : (
                        'Resend'
                    )}
                    </button>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Check your spam folder or{' '}
                <a
                  href="https://www.transtechconnect.com/whitelist"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500"
                >
                  view our email whitelist guide
                </a>
              </p>
            </div>

            {otpError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">{otpError}</p>
              </div>
            )}

{emailSendAttempted && (
            <div className="space-y-3">
              <div className="text-center">
                <button
                  onClick={handleSkipEmailConfirmation}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors underline"
                >
                  Skip for now
                </button>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-xs">
                  <strong>Note:</strong> Email verification will be required if you want to create a new company. 
                  You can still join existing companies or accept invitations without verification.
                </p>
              </div>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
  const AccountStep = () => (
    <div className="max-w-md w-full bg-white rounded-xl p-8 shadow-xl border border-gray-100">

      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <input
              type="text"
              defaultValue={formData.firstName}
              onChange={(e) => {
                inputRefs.current.firstName = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('firstName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors ${
                validationErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="John"
            />
            {validationErrors.firstName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              defaultValue={formData.lastName}
              onChange={(e) => {
                inputRefs.current.lastName = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('lastName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors ${
                validationErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Doe"
            />
            {validationErrors.lastName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
          <input
            type="email"
            defaultValue={formData.email}
            onChange={(e) => {
              inputRefs.current.email = e.target.value;
            }}
            onBlur={(e) => handleInputBlur('email', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors ${
              validationErrors.email || emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="john@company.com"
          />
          {(validationErrors.email || emailError) && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.email || emailError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              defaultValue={formData.password}
              onChange={(e) => {
                inputRefs.current.password = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('password', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors pr-12 ${
                validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Create a strong password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
            </button>
          </div>
          {validationErrors.password && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              defaultValue={formData.confirmPassword}
              onChange={(e) => {
                inputRefs.current.confirmPassword = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('confirmPassword', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors pr-12 ${
                validationErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
            </button>
          </div>
          {validationErrors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.confirmPassword}</p>
          )}
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="acceptTerms"
            checked={formData.acceptTerms}
            onChange={(e) => handleNonTextInputChange('acceptTerms', e.target.checked)}
            className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
          />
          <label htmlFor="acceptTerms" className="ml-3 text-sm text-gray-600">
            I agree to the{' '}
            <a href="https://transtechsys.com/products/privacy" className="text-yellow-600 hover:text-yellow-500 font-medium">Privacy Policy</a>
          </label>
        </div>
        {validationErrors.acceptTerms && (
          <p className="text-red-500 text-xs">{validationErrors.acceptTerms}</p>
        )}

        {validationErrors.general && (
          <p className="text-red-500 text-sm">{validationErrors.general}</p>
        )}

        <button
          onClick={nextStep}
          disabled={isSubmitting}
          className="w-full py-3 bg-gradient-to-r from-yellow-600 to-yellow-500 text-white rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>

      <p className="text-center text-sm text-gray-500 mt-6">
        Already have an account?{' '}
        <a href="/login" className="text-yellow-600 hover:text-yellow-500 font-medium">Sign in</a>
      </p>
      <p className="text-center text-sm text-gray-500 mt-2">
        
        <a href="/" className="text-yellow-600 hover:text-yellow-500 font-medium">Back to home page</a>
      </p>
    </div>
  );

  // Step 3: Company Setup
  const CompanyStep = () => (
    <div className="max-w-lg w-full bg-gradient-to-br from-stone-800/80 to-stone-900/80 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-stone-700/50">
      <div className="text-center mb-8">
        <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 p-4 rounded-full inline-block mb-6 shadow-lg border border-yellow-500/40">
          <SparklesIcon className="w-8 h-8 text-yellow-500" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Choose how to set up your workspace</h3>
      </div>

      <div className="space-y-6">
        <div className="space-y-4">
          <div
            onClick={() => handleNonTextInputChange('companyOption', 'create')}
            className={`p-5 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
              formData.companyOption === 'create'
                ? 'border-yellow-500 bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 shadow-md'
                : 'border-stone-600 hover:border-stone-500'
            }`}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg mr-4 ${
                formData.companyOption === 'create' ? 'bg-yellow-500/20' : 'bg-stone-700'
              }`}>
                <BuildingOfficeIcon className={`w-6 h-6 ${
                  formData.companyOption === 'create' ? 'text-yellow-500' : 'text-gray-400'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white mb-1">Create New Company</h3>
                <p className="text-sm text-gray-300">Start fresh with your own workspace and invite your team</p>
              </div>
              {formData.companyOption === 'create' && (
                <div className="bg-yellow-500 rounded-full p-1">
                  <CheckIcon className="w-4 h-4 text-black" />
                </div>
              )}
            </div>
          </div>

          {pendingInvites.length > 0 && (
            <div
              onClick={() => handleNonTextInputChange('companyOption', 'invite')}
              className={`p-5 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                formData.companyOption === 'invite'
                  ? 'border-blue-500 bg-gradient-to-br from-blue-500/10 to-blue-600/10 shadow-md'
                  : 'border-stone-600 hover:border-stone-500'
              }`}
            >
              <div className="flex items-start">
                <div className={`p-3 rounded-lg mr-4 flex-shrink-0 ${
                  formData.companyOption === 'invite' ? 'bg-blue-500/20' : 'bg-stone-700'
                }`}>
                  <EnvelopeIcon className={`w-6 h-6 ${
                    formData.companyOption === 'invite' ? 'text-blue-400' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">Accept Invitation</h3>
                    {formData.companyOption === 'invite' && (
                      <div className="bg-blue-500 rounded-full p-1 flex-shrink-0">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-stone-700/50 border border-stone-600 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white text-sm">{pendingInvites[0].organizations.name}</p>
                        <p className="text-xs text-gray-400">From {pendingInvites[0].inviter_email}</p>
                      </div>
                      <div className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full border border-green-500/30">
                        14-day trial
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {invitesLoading && (
            <div className="p-5 border-2 border-dashed border-stone-600 rounded-xl bg-stone-800/50">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400 mr-4"></div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Checking for Invitations</h3>
                  <p className="text-sm text-gray-400">Please wait while we check for pending invitations...</p>
                </div>
              </div>
            </div>
          )}

          <div
            onClick={() => handleNonTextInputChange('companyOption', 'join')}
            className={`p-5 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
              formData.companyOption === 'join'
                ? 'border-yellow-500 bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 shadow-md'
                : 'border-stone-600 hover:border-stone-500'
            }`}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg mr-4 ${
                formData.companyOption === 'join' ? 'bg-yellow-500/20' : 'bg-stone-700'
              }`}>
                <UserGroupIcon className={`w-6 h-6 ${
                  formData.companyOption === 'join' ? 'text-yellow-500' : 'text-gray-400'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white mb-1">Join Existing Company</h3>
                <p className="text-sm text-gray-300">Join your team's workspace using an invitation key</p>
              </div>
              {formData.companyOption === 'join' && (
                <div className="bg-yellow-500 rounded-full p-1">
                  <CheckIcon className="w-4 h-4 text-black" />
                </div>
              )}
            </div>
          </div>
        </div>

        {formData.companyOption === 'create' && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">Company Name</label>
            <input
              type="text"
              defaultValue={formData.companyName}
              onChange={(e) => {
                inputRefs.current.companyName = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('companyName', e.target.value)}
              className={`w-full px-4 py-3 bg-stone-800/50 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors text-white placeholder-gray-400 ${
                validationErrors.companyName ? 'border-red-400 bg-red-900/20' : 'border-stone-600'
              }`}
              placeholder="Acme Construction Co."
            />
            {validationErrors.companyName && (
              <p className="text-red-400 text-xs mt-1">{validationErrors.companyName}</p>
            )}
          </div>
        )}

        {formData.companyOption === 'join' && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">Company Invitation Key</label>
            <input
              type="text"
              defaultValue={formData.companyKey}
              onChange={(e) => {
                inputRefs.current.companyKey = e.target.value;
              }}
              onBlur={(e) => handleInputBlur('companyKey', e.target.value)}
              className={`w-full px-4 py-3 bg-stone-800/50 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors font-mono text-white placeholder-gray-400 ${
                validationErrors.companyKey ? 'border-red-400 bg-red-900/20' : 'border-stone-600'
              }`}
              placeholder="Enter invitation key..."
            />
            {validationErrors.companyKey && (
              <p className="text-red-400 text-xs mt-1">{validationErrors.companyKey}</p>
            )}
            <p className="text-xs text-gray-400">Ask your team administrator for the invitation key</p>
          </div>
        )}

        {inviteError && (
          <p className="text-red-400 text-sm">{inviteError}</p>
        )}
        {inviteSuccess && (
          <p className="text-green-400 text-sm">{inviteSuccess}</p>
        )}
        {validationErrors.general && (
          <p className="text-red-400 text-sm">{validationErrors.general}</p>
        )}

{formData.companyOption === 'create' && !otpMessage.includes('confirmed') && (
  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
    <p className="text-yellow-400 text-sm">
      <strong>Email verification required:</strong> To create a company, you must first verify your email address.
      <button 
        onClick={() => setCurrentStep(2)}
        className="ml-2 text-yellow-300 underline hover:text-yellow-200"
      >
        Go back to verify
      </button>
    </p>
  </div>
)}

        <button
          onClick={nextStep}
          disabled={isSubmitting || (formData.companyOption === 'invite' && pendingInvites.length === 0)}
          className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
            (isSubmitting || (formData.companyOption === 'invite' && pendingInvites.length === 0))
              ? 'bg-stone-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-yellow-500 to-yellow-400 text-black hover:from-yellow-400 hover:to-yellow-300'
          }`}
        >
          {isSubmitting 
            ? 'Setting up...' 
            : formData.companyOption === 'create' 
              ? 'Create Company & Start Trial' 
              : formData.companyOption === 'join'
                ? 'Join Company'
                : 'Continue'}
        </button>

        <div className="text-center mt-4">
          <button
            onClick={() => {
              if (onComplete) onComplete();
              navigate('/dashboard');
            }}
            className="text-gray-400 hover:text-gray-300 text-sm font-medium transition-colors underline"
          >
            I'll do this later, go to dashboard
          </button>
        </div>
      </div>
    </div>
  );

  // Step 4: Trial Welcome
  const WelcomeStep = () => (
    <div className="max-w-2xl w-full bg-gradient-to-br from-stone-800/80 to-stone-900/80 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-stone-700/50">
      <div className="text-center mb-8">
        <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-6 rounded-full inline-block mb-6 shadow-lg border border-green-500/40">
          <CheckCircleIcon className="w-16 h-16 text-green-400" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-3">Welcome to TransTech!</h2>
        <p className="text-xl text-gray-300 mb-4">
          {formData.companyOption === 'create' 
            ? `${formData.companyName} is ready to go` 
            : formData.companyOption === 'invite' && formData.acceptedCompanyName
              ? `Welcome to ${formData.acceptedCompanyName}!`
              : 'You\'re now part of the team'}
        </p>
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-green-400 font-medium text-lg">
            🎉 Your 14-day trial is now active!
          </p>
          <p className="text-green-300 text-sm mt-1">
            Full access to all features • No limitations • No credit card required
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="text-center p-6 bg-gradient-to-br from-stone-700/50 to-stone-800/50 rounded-xl border border-stone-600/50 hover:shadow-md transition-all duration-200">
          <div className="bg-yellow-500/20 p-3 rounded-full inline-block mb-4">
            <DocumentChartBarIcon className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="font-semibold text-white mb-2">Professional Reports</h3>
          <p className="text-sm text-gray-300">Generate detailed compaction reports with custom branding</p>
        </div>
        
        <div className="text-center p-6 bg-gradient-to-br from-stone-700/50 to-stone-800/50 rounded-xl border border-stone-600/50 hover:shadow-md transition-all duration-200">
          <div className="bg-yellow-500/20 p-3 rounded-full inline-block mb-4">
            <MapIcon className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="font-semibold text-white mb-2">Site Visualization</h3>
          <p className="text-sm text-gray-300">Interactive maps with GPS tracking and real-time data</p>
        </div>
        
        <div className="text-center p-6 bg-gradient-to-br from-stone-700/50 to-stone-800/50 rounded-xl border border-stone-600/50 hover:shadow-md transition-all duration-200">
          <div className="bg-yellow-500/20 p-3 rounded-full inline-block mb-4">
            <ChartBarIcon className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="font-semibold text-white mb-2">Quality Analytics</h3>
          <p className="text-sm text-gray-300">Advanced quality control and compliance monitoring</p>
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => {
            if (onComplete) onComplete();
            navigate('/dashboard');
          }}
          disabled={isSubmitting}
          className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-400 text-black rounded-lg font-semibold hover:from-yellow-400 hover:to-yellow-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Completing Setup...' : 'Explore Your Dashboard'}
        </button>
        
        <div className="text-center">
          <p className="text-sm text-gray-400 mb-2">Need help getting started?</p>
          <div className="space-x-4">
            <a href="#" className="text-yellow-500 hover:text-yellow-400 text-sm font-medium">
              View Quick Start Guide
            </a>
            <span className="text-gray-600">•</span>
            <a href="#" className="text-yellow-500 hover:text-yellow-400 text-sm font-medium">
              Watch Demo Video
            </a>
            <span className="text-gray-600">•</span>
            <a href="#" className="text-yellow-500 hover:text-yellow-400 text-sm font-medium">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-stone-900 to-black flex flex-col items-center justify-center p-4">
      {/* Enhanced Progress Bar */}
      <div className="w-full max-w-2xl mb-12">
        <div className="flex items-center justify-between relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-5 right-5 h-0.5 bg-stone-700">
            <div 
              className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 transition-all duration-500 ease-out"
              style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
            />
          </div>
          
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                currentStep > step.id 
                  ? 'bg-green-500 text-white shadow-lg transform scale-110' 
                  : currentStep === step.id
                    ? 'bg-yellow-500 text-black shadow-lg transform scale-110'
                    : 'bg-stone-800 text-gray-400 border-2 border-stone-600'
              }`}>
                {currentStep > step.id ? <CheckIcon className="w-6 h-6" /> : step.id}
              </div>
              <div className="text-center mt-3">
                <div className={`text-sm font-medium ${
                  currentStep >= step.id ? 'text-white' : 'text-gray-500'
                }`}>
                  {step.name}
                </div>
                <div className="text-xs text-gray-500 mt-1">{step.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="flex items-center justify-center w-full">
        {currentStep === 1 && <AccountStep />}
        {currentStep === 2 && <EmailConfirmationStep />}
        {currentStep === 3 && <CompanyStep />}
        {currentStep === 4 && <WelcomeStep />}
      </div>
    </div>
  );
};

export default OnboardingWizard;